import { useIntl, FormattedMessage } from 'react-intl';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { useRequest } from 'ahooks';
import { Row, Col, Spin } from 'antd';
import classnames from 'classnames';
import { history, useLocation } from 'umi';
import useMobile from '@/hooks/useMobile';
import { getSession, deletePhsicalDamageParams } from '@/utils/common';
import AmountWithUnit from '@/components/amount-with-unit';
import FoldCard from '@/components/fold-card';
import ProductCard from '@/components/product-card';
import coverageTypeIdToName from '@/components/coverage-type-id-to-name';
import { GetProductList, CreateQuote } from '../service';
import { isUndefined } from '@/utils/common';
import useVehicleFiledsVisiblity from '@/hooks/useVehicleFiledsVisiblity';
import useHandleFormData from '@/hooks/useHandleFormData';
import specialIntlMessages from '@/utils/specialIntlMessages';
import * as SESSION_KEYS from '@/configs/session-storage-keys';
import { images } from '@/assets/images';
import * as constants from '../vehicle-info/constants';
import numeral from 'numeral';

import './index.less';

interface Props {
  productType: TTypes;
  nextStep: string;
  changeActiveStep: any;
}

const ProductPick = (props: Props) => {
  const { productType, changeActiveStep, nextStep } = props;
  const { formatMessage } = useIntl();
  const isMobile = useMobile();
  const { query }: any = useLocation();
  const { totalStep } = query;
  const vehicleInfo = getSession(SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE);

  const { create } = useHandleFormData();

  const [filterBy, setFilterBy] = useState('all');
  const [productGroups, setProductGroups] = useState<{
    [key: string]: any[];
  }>({});

  const visibilities = useVehicleFiledsVisiblity({
    vehicleType: vehicleInfo?.vehicle_type,
  });

  const { loading, run: getProductList } = useRequest<{ plans: any[] }, any>(
    GetProductList,
    {
      manual: true,
      onSuccess: (res) => {
        const plans = res.plans.filter((e) => !!e);

        const insureList: { [key: string]: any } = {};
        plans.forEach((plan) => {
          const { coverage_type } = plan?.addl_data || {};
          if (insureList[coverage_type]) {
            insureList[coverage_type].push(plan);
          } else {
            insureList[coverage_type] = [plan];
          }
        });
        setProductGroups(insureList);
      },
    },
  );

  const { run: createQuote } = useRequest(CreateQuote, {
    onSuccess: (res) => {
      sessionStorage.setItem(SESSION_KEYS.QUOTE_INFO, JSON.stringify(res));
      changeActiveStep(nextStep);
    },
    manual: true,
  });

  useEffect(() => {
    getProductList(productType, vehicleInfo);
  }, []);

  const allVehicleTypes = constants.getVehicleTypes({ formatMessage });
  const motorcycleTypes = allVehicleTypes
    .filter((e) => e.groupBy === 'motorcycle')
    .map((e) => e.name);

  const cardTitleMaps: any = {
    motor: motorcycleTypes.includes(vehicleInfo?.vehicle_type)
      ? formatMessage({
          id: 'Motorcycle Details',
        })
      : formatMessage({
          id: 'Vehicle Details',
        }),
    travel: formatMessage({
      id: 'Travel Details',
    }),
    pa: formatMessage({
      id: 'Policy Details',
    }),
    health: formatMessage({
      id: 'Policy Details',
    }),
  };

  const iconMaps: any = {
    motor: motorcycleTypes.includes(vehicleInfo?.vehicle_type)
      ? images.motorcycle_icon
      : images.vehicle_icon,
    travel: images.travel_icon,
    pa: images.pa_icon,
    health: images.hc_icon,
  };

  const foldCardList = [
    // motor
    {
      label: formatMessage({ id: 'Vehicle Type' }),
      value: vehicleInfo?.vehicle_type ? (
        <FormattedMessage {...specialIntlMessages[vehicleInfo?.vehicle_type]} />
      ) : null,
      hidden: isUndefined(vehicleInfo?.vehicle_type),
    },
    {
      label: formatMessage({ id: 'Business Usage' }),
      value: vehicleInfo?.business_usage ? (
        <FormattedMessage
          {...specialIntlMessages[vehicleInfo?.business_usage]}
        />
      ) : null,
      hidden: !visibilities.business,
    },
    {
      label: formatMessage({ id: 'Number of Seats' }),
      value: vehicleInfo?.seats_number,
      hidden: !visibilities.numberOfSeas,
    },
    {
      label: formatMessage({ id: 'Vehicle Value' }),
      value: (
        <AmountWithUnit
          amount={vehicleInfo?.vehicle_value ?? 0}
          unit={formatMessage({ id: 'VND' })}
        />
      ),
      hidden: isUndefined(vehicleInfo?.vehicle_value),
      alwaysVisible: visibilities.physicalDamage,
    },
    {
      label: formatMessage({ id: 'Vehicle Origin' }),
      value: vehicleInfo?.vehicle_origin ? (
        <FormattedMessage
          {...specialIntlMessages[vehicleInfo?.vehicle_origin]}
        />
      ) : null,
      hidden: isUndefined(vehicleInfo?.vehicle_origin),
      alwaysVisible: visibilities.physicalDamage,
    },
    {
      label: formatMessage({ id: 'Vehicle Manufacture Date' }),
      value: vehicleInfo?.manufacture_date
        ? moment(vehicleInfo?.manufacture_date).format('DD / MM / YYYY')
        : undefined,
      hidden:
        isUndefined(vehicleInfo?.manufacture_date) ||
        vehicleInfo?.manufacture_date === 0,
      alwaysVisible:
        visibilities.physicalDamage &&
        vehicleInfo?.vehicle_origin === 'Imported Car',
    },
    {
      label: formatMessage({ id: 'Vehicle Registration Date' }),
      value: vehicleInfo?.registration_date
        ? moment(vehicleInfo?.registration_date).format('MM / YYYY')
        : undefined,
      hidden:
        isUndefined(vehicleInfo?.registration_date) ||
        vehicleInfo?.registration_date === 0,
      alwaysVisible:
        visibilities.physicalDamage &&
        (vehicleInfo?.vehicle_origin === 'Made in Vietnam' ||
          !vehicleInfo?.vehicle_origin),
    },
    // travel
    {
      label: formatMessage({ id: 'Destination' }),
      value: vehicleInfo?.destination ? (
        <FormattedMessage {...specialIntlMessages[vehicleInfo?.destination]} />
      ) : null,
      hidden: isUndefined(vehicleInfo?.destination),
    },
    {
      label: formatMessage({ id: 'Travel Start Date' }),
      value: vehicleInfo?.travel_start_date
        ? moment(vehicleInfo?.travel_start_date).format('DD / MM / YYYY')
        : undefined,
    },
    {
      label: formatMessage({ id: 'Travel End Date' }),
      value: vehicleInfo?.travel_end_date
        ? moment(vehicleInfo?.travel_end_date).format('DD / MM / YYYY')
        : undefined,
    },
    // hospital cash
    {
      label: formatMessage({ id: 'Policy Duration' }),
      value: vehicleInfo?.policy_duration ? (
        <FormattedMessage
          {...specialIntlMessages[vehicleInfo?.policy_duration]}
        />
      ) : null,
      hidden: isUndefined(vehicleInfo?.policy_duration),
    },
    {
      label: formatMessage({ id: 'No. of Insured' }),
      value: vehicleInfo?.insured_num ? vehicleInfo?.insured_num : null,
      hidden: isUndefined(vehicleInfo?.insured_num),
    },
    // pa
    {
      label: formatMessage({ id: 'Plan' }),
      value: vehicleInfo?.applicants ? (
        <FormattedMessage {...specialIntlMessages[vehicleInfo?.applicants]} />
      ) : null,
      hidden: isUndefined(vehicleInfo?.applicants),
    },
  ].filter((e) => e.alwaysVisible || (!isUndefined(e.value) && !e.hidden));

  return (
    <div
      className={classnames([
        'productPickContainer',
        {
          'is-mobile': useMobile,
        },
      ])}
    >
      <FoldCard
        title={cardTitleMaps[productType]}
        titleIconSrc={iconMaps[productType]}
        list={foldCardList}
      />

      <div className="productContainer">
        {Object.keys(productGroups)?.length > 1 && (
          <Row className={'filterButtons'} gutter={8} wrap={false}>
            <Col onClick={() => setFilterBy('all')} style={{ flexShrink: 0 }}>
              <div
                className={classnames('filterButton', {
                  activeButton: filterBy === 'all',
                })}
              >
                {formatMessage({ id: 'All' })}
              </div>
            </Col>

            {Object.keys(productGroups).map((insureCode) => (
              <Col
                onClick={() => setFilterBy(insureCode)}
                key={insureCode}
                style={{ flexShrink: 0 }}
              >
                <div
                  className={classnames('filterButton', {
                    activeButton: filterBy === insureCode,
                  })}
                >
                  <FormattedMessage {...coverageTypeIdToName[insureCode]} />
                </div>
              </Col>
            ))}
          </Row>
        )}
      </div>

      <Spin spinning={loading}>
        {Object.keys(productGroups)
          .filter((type) => (filterBy === 'all' ? true : type === filterBy))
          .map((type, index) => {
            return (
              <Row
                key={type}
                gutter={isMobile ? [0, 12] : [8, 32]}
                style={
                  index > 0
                    ? { marginTop: `${isMobile ? '12px' : '32px'}` }
                    : {}
                }
              >
                {productGroups[type]?.map((product) => {
                  const { object, addl_data } = product;
                  const { cms_product_detail } = addl_data;
                  const highlights: { [prop: string]: any } =
                    cms_product_detail?.data?.attributes?.highlights;

                  const insurer =
                    cms_product_detail?.data?.attributes?.insurer || {};
                  const logoUrl =
                    insurer?.data?.attributes?.logo?.data?.attributes?.url ||
                    object?.details?.logo_url;
                  const insurerName =
                    insurer?.data?.attributes?.name || addl_data?.insurer_name;
                  const planName =
                    cms_product_detail?.data?.attributes?.planName ||
                    object?.name;

                  const coverage =
                    productType === 'health' && vehicleInfo?.policy_duration
                      ? formatMessage(
                          {
                            id: '{duration} Coverage',
                          },
                          {
                            duration: (
                              <FormattedMessage
                                {...specialIntlMessages[
                                  vehicleInfo?.policy_duration
                                ]}
                              />
                            ),
                          },
                        )
                      : undefined;

                  return (
                    <Col
                      key={object.id}
                      span={isMobile ? 24 : 8}
                      onClick={() => {
                        sessionStorage.setItem(
                          SESSION_KEYS.STILL_NEED_CACHE,
                          'true',
                        );
                        history.replace({
                          pathname: `/home/${productType}/product-list/${object.product_key}`,
                          query: {
                            totalStep: '3',
                            nextActiveStep: 'confirmation',
                            planKey: object.plan_key,
                            coverageType: addl_data?.coverage_type,
                            soiCategory: addl_data?.soi_category,
                            backTo: `/home/${productType}/quote?totalStep=3&currentActiveStep=productSelection`,
                          },
                          state: {
                            from: 'quote',
                            pathname: `/home/${productType}/quote`,
                            query: {
                              totalStep: '3',
                              currentActiveStep: 'productSelection',
                            },
                          },
                        });
                      }}
                    >
                      <ProductCard
                        icon={logoUrl}
                        description={highlights}
                        productName={planName}
                        insurerName={insurerName}
                        productKey={object.product_key}
                        planKey={object.plan_key}
                        totalStep={totalStep}
                        price={
                          <AmountWithUnit
                            amount={addl_data.premium_amount}
                            unit={formatMessage({ id: 'VND' })}
                          />
                        }
                        commission={numeral(addl_data.commission_rate).format(
                          '0.[00]%',
                        )}
                        coverage={coverage}
                        buttonOnClick={() => {
                          sessionStorage.setItem(
                            SESSION_KEYS.PICKED_PLAN_INFO,
                            JSON.stringify({
                              planIcon: object.details.logo_url,
                              planKey: object.plan_key,
                              planName: object.name,
                              productKey: object.product_key,
                              coverageType: addl_data?.coverage_type,
                              soiCategory: addl_data?.soi_category,
                            }),
                          );
                          let vehicleInfoJSON = create(
                            vehicleInfo as { [prop: string]: any },
                          );
                          if (
                            productType === 'motor' &&
                            addl_data?.coverage_type === 'tpl'
                          ) {
                            vehicleInfoJSON =
                              deletePhsicalDamageParams(vehicleInfoJSON);
                          }
                          createQuote(
                            productType,
                            object.product_key,
                            object.plan_key,
                            vehicleInfoJSON,
                          );
                        }}
                      />
                    </Col>
                  );
                })}
              </Row>
            );
          })}
      </Spin>
    </div>
  );
};

export default ProductPick;
